import { HttpClient } from '@adapters/HttpClient';
import { CreateStockAdjustmentRequest, WarehouseMaterialLocation } from '../models/WarehouseMaterialLocation';

const WAREHOUSE_LOCATION_TYPE_ID = 6;

function mapLocation(raw: { id: number; code?: string; stock?: number }): WarehouseMaterialLocation {
  return {
    id: raw.id,
    code: raw.code ?? `#${raw.id}`,
    stock: typeof raw.stock === 'number' ? raw.stock : 0,
  };
}

function extractLocations(response: unknown): WarehouseMaterialLocation[] {
  const list = Array.isArray(response) ? response : ((response as { data?: unknown[] })?.data ?? []);

  return list.map((item) => mapLocation(item as { id: number; code?: string; stock?: number }));
}

export class WarehouseMaterialStockAdapter {
  constructor(private httpClient: HttpClient) {}

  async getLocationsByProduct(productId: number): Promise<WarehouseMaterialLocation[]> {
    const response = await this.httpClient.get<unknown>(
      '/locations',
      {
        type_ids: WAREHOUSE_LOCATION_TYPE_ID,
        product_ids: productId,
        per_page: 1,
      },
      {},
      false,
    );

    return extractLocations(response);
  }

  async getEmptyWarehouseLocations(): Promise<WarehouseMaterialLocation[]> {
    const response = await this.httpClient.get<unknown>(
      '/locations',
      {
        per_page: 50,
        'advanced_filters[logic]': 'AND',
        'advanced_filters[filters][0][column]': 'type_id',
        'advanced_filters[filters][0][operator]': 'in',
        'advanced_filters[filters][0][value][]': String(WAREHOUSE_LOCATION_TYPE_ID),
        'advanced_filters[filters][1][column]': 'product_id',
        'advanced_filters[filters][1][operator]': 'is_null',
      },
      {},
      false,
    );

    return extractLocations(response);
  }

  async adjustStock(data: CreateStockAdjustmentRequest): Promise<void> {
    if (data.quantity === 0) return;

    const isIncrease = data.quantity > 0;

    await this.httpClient.post('/stock-movement', {
      event_type: isIncrease ? 'stock_adjustment_increase' : 'stock_adjustment_decrease',
      product_id: data.product_id,
      quantity: Math.abs(data.quantity),
      ...(isIncrease ? { target_location_id: data.location_id } : { source_location_id: data.location_id }),
    });
  }
}
